import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// Available charts and tables that can be inserted
// Matching PDF report format (Gráfico 04-09, Cuadro 06)
// Note: grafico10 (Eficiencia y Gestión) is disabled - data not available yet
const AVAILABLE_CHARTS = [
  { id: 'grafico04', name: 'Ingresos/Activo Productivo' },
  { id: 'grafico05', name: 'Intereses Causados/Activo Productivo' },
  { id: 'grafico06', name: 'Composición del Patrimonio' },
  { id: 'grafico07', name: 'Solvencia' },
  { id: 'grafico08', name: 'Liquidez General' },
  { id: 'grafico09', name: 'Riesgo de Crédito' },
];

const AVAILABLE_TABLES = [
  { id: 'cuadro06', name: 'Evolución Balance' },
];

interface AssetsSectionProps {
  onInsertAsset: (assetRef: string) => void;
  usedAssets?: string[]; // List of asset IDs currently used in the report
  isReadOnly?: boolean; // Disable insertion when in read-only or preview mode
}

export default function AssetsSection({ onInsertAsset, usedAssets = [], isReadOnly = false }: AssetsSectionProps) {
  const { t } = useTranslation();
  const [chartsExpanded, setChartsExpanded] = useState(false);
  const [tablesExpanded, setTablesExpanded] = useState(false);

  const handleInsertChart = (chartId: string) => {
    onInsertAsset(`[CHART:${chartId}]`);
  };

  const handleInsertTable = (tableId: string) => {
    onInsertAsset(`[TABLE:${tableId}]`);
  };

  // Check if an asset is used in the report
  const isAssetUsed = (assetId: string) => usedAssets.includes(assetId);

  // Count used assets
  const usedChartsCount = AVAILABLE_CHARTS.filter(c => isAssetUsed(c.id)).length;
  const usedTablesCount = AVAILABLE_TABLES.filter(t => isAssetUsed(t.id)).length;

  return (
    <div className="assets-section">
      <h3 className="assets-header">{t('editorAssets.title')}</h3>

      {/* Charts accordion */}
      <div className="asset-group">
        <button
          className={`asset-group-header ${chartsExpanded ? 'expanded' : ''}`}
          onClick={() => setChartsExpanded(!chartsExpanded)}
        >
          <span className="expand-icon">{chartsExpanded ? '▼' : '▶'}</span>
          <span>{t('editorAssets.charts')}</span>
          <span className="asset-count">{usedChartsCount}/{AVAILABLE_CHARTS.length}</span>
        </button>
        {chartsExpanded && (
          <ul className="asset-list">
            {AVAILABLE_CHARTS.map((chart) => {
              const isUsed = isAssetUsed(chart.id);
              return (
                <li
                  key={chart.id}
                  className={`asset-item ${isUsed ? 'used' : ''} ${isReadOnly ? 'disabled' : ''}`}
                  onClick={() => !isReadOnly && handleInsertChart(chart.id)}
                  title={isReadOnly ? t('editorAssets.switchToEditCharts') : `Click to insert ${chart.name}`}
                >
                  <span className="asset-indicator">{isUsed ? '●' : '○'}</span>
                  <span className="asset-name">{chart.name}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Tables accordion */}
      <div className="asset-group">
        <button
          className={`asset-group-header ${tablesExpanded ? 'expanded' : ''}`}
          onClick={() => setTablesExpanded(!tablesExpanded)}
        >
          <span className="expand-icon">{tablesExpanded ? '▼' : '▶'}</span>
          <span>{t('editorAssets.tables')}</span>
          <span className="asset-count">{usedTablesCount}/{AVAILABLE_TABLES.length}</span>
        </button>
        {tablesExpanded && (
          <ul className="asset-list">
            {AVAILABLE_TABLES.map((table) => {
              const isUsed = isAssetUsed(table.id);
              return (
                <li
                  key={table.id}
                  className={`asset-item ${isUsed ? 'used' : ''} ${isReadOnly ? 'disabled' : ''}`}
                  onClick={() => !isReadOnly && handleInsertTable(table.id)}
                  title={isReadOnly ? t('editorAssets.switchToEditTables') : `Click to insert ${table.name}`}
                >
                  <span className="asset-indicator">{isUsed ? '●' : '○'}</span>
                  <span className="asset-name">{table.name}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
